import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { credentials, type Subscription } from '../api/client';
import { useToast } from './Toast';
import { Button, Field, Input, Modal, Textarea } from './ui';

/**
 * Credentials are write-only at rest: storing replaces the ciphertext, and the
 * only way to read them back is this explicit GET — which is audit-logged
 * (`credential_viewed`). The dialog therefore never fetches on open.
 */
export function CredentialsDialog({
  open,
  subscription,
  onClose,
}: {
  open: boolean;
  subscription: Subscription | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [extra, setExtra] = useState('');
  const [revealed, setRevealed] = useState(false);

  const reset = () => {
    setLogin('');
    setPassword('');
    setExtra('');
    setRevealed(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const view = useQuery({
    queryKey: ['credentials', subscription?.id, 'view'],
    queryFn: ({ signal }) => credentials.view(subscription?.id ?? '', signal),
    enabled: open && revealed && Boolean(subscription),
    // Never cache a decryption: a second reveal is a second, audited GET.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  const save = useMutation({
    mutationFn: () =>
      credentials.set(subscription?.id ?? '', {
        login: login.trim(),
        password,
        extra: extra.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      toast(`Credentials stored for ${subscription?.name ?? 'subscription'}`, 'success');
      reset();
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: () => credentials.remove(subscription?.id ?? ''),
    onSuccess: () => {
      toast('Credentials removed', 'success');
      reset();
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  if (!subscription) return null;
  const data = view.data;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Credentials"
      description={`${subscription.name} — encrypted at rest (AES-256-GCM)`}
      footer={
        <>
          <Button variant="danger" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>
            Remove stored credentials
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
        </>
      }
    >
      <section>
        <h3 className="section-title">Reveal (audit-logged)</h3>
        {revealed ? (
          <div className="cred">
            {view.isPending ? (
              <p className="cred__note">Decrypting…</p>
            ) : view.isError ? (
              <p className="cred__note">
                {view.error instanceof Error ? view.error.message : 'No stored credentials.'}
              </p>
            ) : data ? (
              <>
                <CredentialRow label="Login" value={data.login} />
                <CredentialRow label="Password" value={data.password} secret />
                {data.extra ? <CredentialRow label="Extra" value={data.extra} /> : null}
              </>
            ) : (
              <p className="cred__note">No stored credentials.</p>
            )}
            <p className="cred__note">
              Viewing is recorded in the audit log with the subscription id and timestamp. Values are
              hidden again when this dialog closes.
            </p>
            <Button size="sm" variant="secondary" onClick={() => setRevealed(false)}>
              Hide
            </Button>
          </div>
        ) : (
          <div>
            <Button variant="secondary" onClick={() => setRevealed(true)}>
              Show credentials
            </Button>
            <p className="cred__note">Nothing is decrypted until you click — the read is audit-logged.</p>
          </div>
        )}
      </section>

      <section>
        <h3 className="section-title">Replace</h3>
        <Field label="Login" htmlFor="cred-login">
          <Input id="cred-login" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="off" />
        </Field>
        <Field label="Password" htmlFor="cred-password">
          <Input
            id="cred-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Extra" htmlFor="cred-extra" hint="TOTP seed, recovery codes, notes — encrypted too">
          <Textarea id="cred-extra" value={extra} onChange={(event) => setExtra(event.target.value)} />
        </Field>
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!login.trim() || !password}
          onClick={() => save.mutate()}
        >
          Store credentials
        </Button>
      </section>
    </Modal>
  );
}

function CredentialRow({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(!secret);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="cred__row">
      <span className="cred__label">{label}</span>
      <span className="cred__value">{visible ? value : '••••••••'}</span>
      {secret ? (
        <Button size="sm" variant="ghost" onClick={() => setVisible((current) => !current)}>
          {visible ? 'Mask' : 'Unmask'}
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
